import { useCallback, useMemo } from 'react';

import {
  messageDepositCredited,
  messageDepositFailed,
} from '#/components/layout/notifications/notificationsRender';
import { displayBasicNotification } from '#/components/layout/notifications/notificationsUtils';
import config from '#/config';
import { LOOPBACK_FROM_WALLET_TO_VAULT_BRIDGE_SOURCE } from '#/config/constants';
import { filterFalsy } from '#/lib/utils';
import { useSelector } from '#/reduxState/store';

import { updatePendingDepositConfirmations } from './pendingDeposits';

import type { ApiDeposit } from '#/api/types';
import type { PendingDepositItem } from './pendingDepositTypes';

const blockConfirmationDelay = config.chains.katanaChain.blocksConfirmation;

/**
 * Stored loopback quantity is a net estimate - the withdrawal `quantity` it
 * is derived from is gross (credited amount is further reduced by the
 * withdrawal gas fee) and the vault deposit fee deduction can differ from
 * the estimate, so match with a deliberately wide tolerance range. False
 * positives are constrained by the same-vault + credited-after-pending
 * checks in {@link isLoopbackPendingDepositMatch}.
 */
const LOOPBACK_QUANTITY_MATCH_MIN_MULTIPLIER = 0.7;
const LOOPBACK_QUANTITY_MATCH_MAX_MULTIPLIER = 1.3;
// const LOOPBACK_QUANTITY_MATCH_MIN_MULTIPLIER = 0.9;
// const LOOPBACK_QUANTITY_MATCH_MAX_MULTIPLIER = 1.02;

/**
 * Approximate quantity check for loopback deposits (their `quantity` is a
 * net estimate - see {@link isLoopbackPendingDepositMatch})
 */
export const isLoopbackQuantityApproxMatch = (
  pending: PendingDepositItem,
  receivedQuantity: number
) => {
  const expectedQuantity = Number(pending.quantity);
  return (
    receivedQuantity >=
      expectedQuantity * LOOPBACK_QUANTITY_MATCH_MIN_MULTIPLIER &&
    receivedQuantity <=
      expectedQuantity * LOOPBACK_QUANTITY_MATCH_MAX_MULTIPLIER
  );
};

/**
 * Loopback (from exchange balance) vault deposits have no on-chain source TX
 * to match against API records. Instead we match a wallet-to-vault loopback
 * deposit into the same vault, credited after the pending deposit was
 * created and with quantity close to the net estimate.
 */
export const isLoopbackPendingDepositMatch = (
  pending: PendingDepositItem,
  depositInApi: ApiDeposit
) => {
  if (!pending.loopback || !pending.vault?.manager) {
    return false;
  }
  if (
    depositInApi.bridgeSource !== LOOPBACK_FROM_WALLET_TO_VAULT_BRIDGE_SOURCE ||
    depositInApi.vault?.manager.toLowerCase() !==
      pending.vault.manager.toLowerCase()
  ) {
    return false;
  }
  return (
    isLoopbackQuantityApproxMatch(pending, Number(depositInApi.quantity)) &&
    // Do not match deposits older than the pending one (allow small clock skew)
    depositInApi.time >= pending.time - 60_000
  );
};

/** Minimal deposit shape shared by the vault deposit history endpoints */
type VaultHistoryDepositLike = {
  depositId?: string;
  time: number;
  quantity: string;
  bridgeTxId?: string | null;
  forwarderTxId?: string | null;
  katanaTxId?: string | null;
};

/**
 * Match a pending vault deposit against a record from the vault's deposit
 * history - used to resolve pending items directly from already-fetched
 * vault history data. Items must be scoped to the depositor first
 * (depositor-scoped endpoint, or filtered by the wallet field).
 */
export const isVaultHistoryDepositMatch = (
  pending: PendingDepositItem,
  item: VaultHistoryDepositLike
) => {
  if (!item.depositId) {
    return false;
  }
  if (pending.loopback) {
    // No on-chain source TX for loopback deposits - approximate match:
    // credited after the pending was created (small clock skew allowed) with
    // quantity close to the net estimate. The time check is a lower bound
    // only - a delayed credit time always still matches.
    return (
      item.time >= pending.time - 60_000 &&
      isLoopbackQuantityApproxMatch(pending, Number(item.quantity))
    );
  }
  const apiTx =
    item.bridgeTxId?.toLowerCase() ||
    item.forwarderTxId?.toLowerCase() ||
    item.katanaTxId?.toLowerCase();
  return (
    apiTx !== undefined &&
    (pending.bridgeTxId?.toLowerCase() === apiTx ||
      pending.sourceTxId?.toLowerCase() === apiTx)
  );
};

/** Get pending deposits list for active chain */
export const useSelectPendingDepositsListIncludingFailed = (filter?: {
  vaultManagerWallet: string;
}) => {
  const { pendingMap, pendingTxHashes } = useSelector(
    state => state.balances.pendingDeposits
  );
  return useMemo(() => {
    const pendingList = pendingTxHashes
      ? pendingTxHashes.map(hash => pendingMap[hash]).filter(filterFalsy)
      : [];
    if (!filter?.vaultManagerWallet) {
      return pendingList;
    }
    // Active filter
    return pendingList.filter(
      item =>
        item.vault?.manager?.toLowerCase() ===
        filter?.vaultManagerWallet.toLowerCase()
    );
  }, [pendingMap, pendingTxHashes, filter]);
};

export const useSelectPendingDepositListExcludingFailed = () => {
  const pendingDepositsList = useSelectPendingDepositsListIncludingFailed();
  const { getTransactionConfirmations } = usePendingDepositsConfirmations();
  return useMemo(
    () =>
      pendingDepositsList.filter(
        // Exclude failed deposits (-1 confirmations)
        item =>
          getTransactionConfirmations(item.sourceTxId).confirmationCount >= 0
      ),
    [getTransactionConfirmations, pendingDepositsList]
  );
};

export const usePendingDepositsConfirmations = () => {
  const depositsConfirmationsCounts = useSelector(
    state => state.balances.pendingDeposits.confirmationsCounts
  );

  const getTransactionConfirmations = useCallback(
    (transactionHash?: string | null) => {
      if (!transactionHash) {
        return {
          confirmationCount: 0,
          cappedConfirmationCount: 0,
          confirmationRate: 0,
        };
      }
      const confirmationCount =
        depositsConfirmationsCounts[transactionHash] || 0;
      const cappedConfirmationCount: number | undefined =
        transactionHash && blockConfirmationDelay
          ? Math.min(confirmationCount, blockConfirmationDelay)
          : undefined;
      const confirmationRate = blockConfirmationDelay
        ? (cappedConfirmationCount || 0) / blockConfirmationDelay
        : undefined;
      return {
        confirmationCount,
        cappedConfirmationCount,
        confirmationRate,
      };
    },
    [depositsConfirmationsCounts]
  );
  return {
    depositsConfirmationsCounts,
    blockConfirmationDelay,
    getTransactionConfirmations,
  };
};

export const notifyAboutCompletedDeposit = (quantity: string) => {
  return displayBasicNotification(
    messageDepositCredited({ time: Date.now(), quantity }),
    `depositCredited:${quantity}`,
    'success'
  );
};

export const notifyAndUpdatePendingDepositStatusToFailed = (txHash: string) => {
  displayBasicNotification(messageDepositFailed(), 'error');
  return updatePendingDepositConfirmations(txHash, -1);
};
